import {
  belgeCipi,
  HAREKET_TURU_METNI,
  KALDIRMA_NEDENI_METNI,
  KUTUK_KULLANIM_METNI,
  PAKET_BITTI_EKI,
  paketSecenekMetni,
  stokSecenekleri,
  type BelgeCipiGirdisi,
  type HareketTuru,
  type KaldirmaNedeni,
  type KullanimOzeti,
  type OlasiEslesmeGerekcesi,
  type PaketSecenegi,
  type StokEslesmeKalemi,
  type StokPnCifti,
} from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { tarih } from "@/lib/veri";

/**
 * D-045 … D-052: stok paketi metinlerinin ekrandaki biçimi (seçim listeleri, Registers → Stock, belge çipleri).
 * Metinler alan paketinde İngilizce; burada `cevir` ve en-GB / tr-TR tarih ile gösterilir.
 */

/** Seçenek metninde tanımın en çok uzunluğu (uzun tanımlar "…" ile kısalır). */
export const PAKET_TANIM_EN_COK = 60;

/** Seçenek metni: "PN — tanım · <iz> · PO <no> · <kalan> <birim> · opening stock | arrived <tarih> · PKG-####". */
export const paketEtiketi = (p: PaketSecenegi) =>
  paketSecenekMetni(p, { cevir: (m, ...a) => cevir(m, ...a), tarih: (d) => tarih(d), tanimEnCok: PAKET_TANIM_EN_COK });

/** Kaldırılmış kullanımın nedeni ("selection changed" …); bilinmeyen değer olduğu gibi. */
export const kaldirmaNedeniMetni = (n: string | null) =>
  n === null ? "" : n in KALDIRMA_NEDENI_METNI ? cevir(KALDIRMA_NEDENI_METNI[n as KaldirmaNedeni]) : n;

/** Miktar geçmişinin tür adı ("opening stock", "arrival" …); bilinmeyen değer olduğu gibi. */
export const hareketTuruMetni = (t: string) => (t in HAREKET_TURU_METNI ? cevir(HAREKET_TURU_METNI[t as HareketTuru]) : t);

/** Kütük kullanım özeti: "used in 2 fit-ups · used in 1 pass record". */
export const kullanimMetni = (o: KullanimOzeti) =>
  o.parcalar
    .map((p) => {
      const [tekil, cogul] = KUTUK_KULLANIM_METNI[p.tur];
      return p.n === 1 ? cevir(tekil) : cevir(cogul, p.n);
    })
    .join(" · ");

/**
 * D-052: fit-up / weld map yeni malzemesinin "—" sonrası eki: paket → etiket (alan paketinin PAKET_BITTI_EKI eki ayrılıp
 * "(package finished)" çevrilir; PN, PO ve PKG no. çevrilmez); elle → sabit metin; eski biçim → etiket varsa o.
 */
export function malzemeEki(m: { sec: string; etiket?: string }): string | null {
  if (m.sec === "elle") return cevir("entered manually · not linked to a stock package");
  if (!m.etiket) return null;
  return m.etiket.endsWith(PAKET_BITTI_EKI) ? `${m.etiket.slice(0, -PAKET_BITTI_EKI.length)} ${cevir("(package finished)")}` : m.etiket;
}

/**
 * Belge çipi ("3.1 · p. 41–43", "PO 2025-0417" …); kısa tür çevrilir, tarih yerel biçimde.
 * D-064: yöntemi yazılı NDT sertifikasında "MT · Level 2 · MT-L2-0418 · p. 1" (yöntem çevrilmez, "Level 2" çevrilir); yöntemi boş eski satırda "NDT certificate · …".
 */
export const belgeCipiMetni = (b: BelgeCipiGirdisi) => belgeCipi(b, { cevir: (m) => cevir(m), tarih: (d) => tarih(d) });

/**
 * D-076: olası eşleşmenin gri gerekçesi, eşleşmeyi doğuran ilk yol: (a) "PN {0} is in the Item's text" · (b) "OEM PN {0} = OSI PN {1}
 * on FM-0012" · (c) "PN {0} is written for OEM PN {1} on {2}" ({2} kaynak kalem, "R025 Fastener #77"). PN'ler ve kaynak çevrilmez.
 */
export function olasiGerekceMetni(g: OlasiEslesmeGerekcesi): string {
  if (g.yol === "metin") return cevir("PN {0} is in the Item's text", g.pn);
  if (g.yol === "fm12") return cevir("OEM PN {0} = OSI PN {1} on FM-0012", g.oemPn, g.osiPn);
  return cevir("PN {0} is written for OEM PN {1} on {2}", g.pn, g.siparisPn, g.kaynak);
}

/** D-076: listenin seçim satırı ve kutusu için ortak alanlar. */
interface OlasiListe<T> {
  /** "Possible matches" (aktif paketler; PN sırası, aynı PN'de sunucu sırası) */
  olasi: T[];
  /** "Other stock packages": kutu işaretliyken bütün öteki aktif paketler; değilse yalnız listede kalması gereken paket(ler) */
  diger: T[];
  /** listede seçili paket (bütün listeden aranır); yoksa null */
  secili: T | null;
  /** seçili paket olası eşleşmeyse gerekçesi ("possible match"), değilse null ("not a possible match") */
  gerekce: OlasiEslesmeGerekcesi | null;
  /** "Show all stock packages ({0})" kutusu: varsayılan listede görünmeyen en az bir paket varken (kutu işaretliyken de durur) */
  kutu: boolean;
  /** kutunun {0}'ı */
  toplam: number;
  /** "No stock package with material left matches this Item's part number." */
  eslesmeYok: boolean;
}

/**
 * D-076 · "From stock" (Waiting Items, işaretli salt değişim kalemi; D-050'nin listesi). Varsayılan liste "— not from stock —" ve
 * "Possible matches"tir; öteki aktif paketler kutu işaretliyken "Other stock packages"a gelir. Seçili paket her zaman listede kalır:
 * olası eşleşme değilse ve kutu kapalıysa "Other stock packages"ta tek başına görünür. Kutunun sayısı bütün aktif paketlerdir. Eşleşme
 * yok cümlesi "Possible matches" boşken ve en az bir aktif paket varken çıkar; aktif paket yoksa kutu ve cümle yoktur (bugünkü hâl).
 */
export function stoktanListesi<T extends { id: string; pn: string }>(
  kalem: StokEslesmeKalemi,
  paketler: readonly T[],
  ciftler: readonly StokPnCifti[],
  secimId: string,
  hepsi: boolean,
): OlasiListe<T> {
  const { olasi, diger, gerekceler } = stokSecenekleri(kalem, paketler, ciftler);
  const secili = (secimId && paketler.find((p) => p.id === secimId)) || null;
  return {
    olasi,
    diger: hepsi ? diger : diger.filter((p) => p.id === secili?.id),
    secili,
    gerekce: secili ? (gerekceler.get(secili.pn) ?? null) : null,
    kutu: diger.some((p) => p.id !== secili?.id),
    toplam: paketler.length,
    eslesmeYok: olasi.length === 0 && paketler.length > 0,
  };
}

/**
 * D-076 · "Change stock package" (değiştirilmiş salt değişim kalemi; D-050 ve D-046'nın listesi). Sıra: "Possible matches", "Other stock
 * packages" (kutu kapalıyken yalnız kalemin bugünkü paketi ve pencerede seçili paket, olası eşleşme değillerse), "Finished packages
 * (material already taken)" (kutu kapalıyken yalnız olası eşleşen PN'lerin biten paketleri ile bitmişse bugünkü ve seçili paket; kutu
 * işaretliyken bütün biten paketler). Kutunun sayısı aktif ve biten bütün paketlerdir. Eşleşme yok cümlesi "Possible matches" boşken,
 * biten grubunda da olası eşleşen bir PN'in paketi yokken ve en az bir paket varken çıkar. `secimId` pencerenin değeridir ("" stoktan
 * değil, "mevcut" yer tutucu): yalnız paket seçiliyken `secili` doludur.
 */
export function paketDegisimListesi<T extends { id: string; pn: string }>(
  kalem: StokEslesmeKalemi,
  paketler: readonly T[],
  bitenPaketler: readonly T[],
  ciftler: readonly StokPnCifti[],
  g: { mevcutId: string | null; secimId: string; hepsi: boolean },
): OlasiListe<T> & { biten: T[] } {
  const aktif = stokSecenekleri(kalem, paketler, ciftler);
  const bitmis = stokSecenekleri(kalem, bitenPaketler, ciftler);
  /** listede her zaman kalanlar: kalemin bugünkü paketi ve pencerede seçili paket */
  const kalan = new Set([g.mevcutId, g.secimId].filter((x): x is string => Boolean(x)));
  const bitenOlasi = new Set(bitmis.olasi.map((p) => p.id));
  const secili = (g.secimId && [...paketler, ...bitenPaketler].find((p) => p.id === g.secimId)) || null;
  const varsayilanDiger = aktif.diger.filter((p) => kalan.has(p.id));
  const varsayilanBiten = bitenPaketler.filter((p) => bitenOlasi.has(p.id) || kalan.has(p.id));
  return {
    olasi: aktif.olasi,
    diger: g.hepsi ? aktif.diger : varsayilanDiger,
    biten: g.hepsi ? [...bitenPaketler] : varsayilanBiten,
    secili,
    gerekce: secili ? (aktif.gerekceler.get(secili.pn) ?? bitmis.gerekceler.get(secili.pn) ?? null) : null,
    kutu: aktif.diger.length > varsayilanDiger.length || bitenPaketler.length > varsayilanBiten.length,
    toplam: paketler.length + bitenPaketler.length,
    eslesmeYok: aktif.olasi.length === 0 && bitmis.olasi.length === 0 && paketler.length + bitenPaketler.length > 0,
  };
}

export type { PaketSecenegi };
